/**
 * Login.js — the sign-in gate (screen `login`).
 *
 * Rendered by App.js before the shell when Supabase is configured, so it sits
 * outside the router and the query client: no <Link>, no queries, no sidebar,
 * no signed-in avatar. It must render on its own.
 *
 * Sign-in methods are exactly the ones this code implements today: Google and
 * Microsoft (Azure) OAuth through Supabase. Nothing else is offered.
 *
 * Behaviour kept from the previous version:
 *   - an implicit-flow redirect that lands here with a session in the URL hash
 *     is sent on to '/';
 *   - OAuth returns to window.location.origin (there is no return-path support
 *     in the current auth flow, so none is invented here).
 * Added: a synchronous guard so a double click can't start two OAuth
 * redirects, and the provider's error (?error_description= / #error_description=)
 * is shown when a sign-in attempt comes back failed, with the buttons ready to
 * try again.
 */
import React, { useEffect, useRef, useState } from 'react'
import { getSupabase } from '../lib/supabase'
import Icon from '../components/Icon'
import { Spinner } from '../components/ui'
import '../styles/entry-flow.css'

const PROVIDERS = {
  google: 'Google',
  azure: 'Microsoft',
}

// Supabase reports a failed OAuth round trip in the query string or the hash.
function readCallbackError() {
  if (typeof window === 'undefined') return ''
  try {
    const sources = [
      new URLSearchParams(window.location.search),
      new URLSearchParams((window.location.hash || '').replace(/^#/, '')),
    ]
    for (const p of sources) {
      const msg = p.get('error_description') || p.get('error')
      if (msg) return msg
    }
  } catch { /* malformed URL — nothing to report */ }
  return ''
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.859-3.048.859-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.705A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.705V4.963H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.037l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.963L3.964 7.295C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  )
}

function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <path fill="#F25022" d="M1 1h9v9H1z"/>
      <path fill="#7FBA00" d="M11 1h9v9h-9z"/>
      <path fill="#00A4EF" d="M1 11h9v9H1z"/>
      <path fill="#FFB900" d="M11 11h9v9h-9z"/>
    </svg>
  )
}

const BENEFITS = [
  { icon: 'record',  title: 'Capture your process',    body: 'Record your screen, upload a video or bring in a Loom as you work.' },
  { icon: 'file',    title: 'Shape clear steps',       body: 'Flow turns the recording into an editable, step-by-step guide.' },
  { icon: 'publish', title: 'Publish where teams work', body: 'Send finished guides to your hosted docs site, Freshdesk or Confluence.' },
]

export default function Login() {
  const [loading, setLoading] = useState(false)
  const [pending, setPending] = useState('')          // provider key being started
  const [error,   setError]   = useState('')
  const [callbackError] = useState(readCallbackError)
  const startingRef = useRef(false)                    // blocks a second start before re-render

  // Handle implicit flow redirect — session arrives in URL hash
  useEffect(() => {
    const client = getSupabase()
    if (!client) return
    client.auth.getSession().then(({ data: { session } }) => {
      if (session) window.location.replace('/')
    })
  }, [])

  async function handleSignIn(provider = 'google') {
    if (startingRef.current) return
    startingRef.current = true
    setError('')
    setLoading(true)
    setPending(provider)
    const client = getSupabase()
    if (!client) {
      setError('Auth not configured.')
      setLoading(false); setPending(''); startingRef.current = false
      return
    }
    try {
      const { error: err } = await client.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin,
          ...(provider === 'azure' ? { scopes: 'openid email profile' } : {}),
        },
      })
      if (err) {
        setError(err.message)
        setLoading(false); setPending(''); startingRef.current = false
      }
    } catch (e) {
      setError(e?.message || 'Could not start sign-in. Try again.')
      setLoading(false); setPending(''); startingRef.current = false
    }
  }

  const providerButton = (key, mark) => {
    const isPending = pending === key
    return (
      <button
        type="button"
        className="btn en-provider-btn"
        onClick={() => handleSignIn(key)}
        disabled={loading}
        aria-busy={isPending || undefined}
      >
        {isPending ? <Spinner size={18} /> : mark}
        {isPending ? `Redirecting to ${PROVIDERS[key]}…` : `Continue with ${PROVIDERS[key]}`}
      </button>
    )
  }

  return (
    <div className="en-login">
      <div className="en-login-frame">
        <div className="en-login-brand">
          <div className="logo-mark" aria-hidden="true">AF</div>
          <div>
            <div className="logo-name">Flow</div>
            <div className="logo-sub">by Affixly</div>
          </div>
        </div>

        <main className="en-login-grid">
          <section className="en-login-card" aria-labelledby="en-login-title">
            <h1 id="en-login-title" className="en-login-title">Welcome to Flow</h1>
            <p className="en-login-lede">Sign in to turn your recordings into step-by-step guides.</p>

            {callbackError && !error && (
              <div className="inline-msg is-error" role="alert">
                <Icon name="alert" />
                <span>Sign-in didn't complete: {callbackError}. Choose a sign-in option to try again.</span>
              </div>
            )}

            <div className="en-provider-list">
              {providerButton('google', <GoogleMark />)}
              {providerButton('azure', <MicrosoftMark />)}
            </div>

            {error && (
              <div className="inline-msg is-error" role="alert">
                <Icon name="alert" />
                <span>{error}</span>
              </div>
            )}
            <p className="visually-hidden" role="status">
              {pending ? `Redirecting to ${PROVIDERS[pending]} to sign in.` : ''}
            </p>
          </section>

          <section className="en-login-value" aria-labelledby="en-value-title">
            <h2 id="en-value-title" className="en-value-title">From a recording to a useful guide.</h2>
            <p className="en-value-lede">Turn everyday work into clear, shareable documentation.</p>
            <ul className="en-benefits">
              {BENEFITS.map(b => (
                <li key={b.title}>
                  <span className="en-benefit-icon"><Icon name={b.icon} size={22} /></span>
                  <span>
                    <span className="en-benefit-title">{b.title}</span>
                    <span className="en-benefit-body">{b.body}</span>
                  </span>
                </li>
              ))}
            </ul>

            {/* Decorative: an abstract numbered guide. No real content. */}
            <div className="en-illustration" aria-hidden="true">
              {[1, 2, 3].map(n => (
                <div key={n} className={`en-illu-row ${n === 1 ? 'is-active' : ''}`}>
                  <span className="en-illu-num">{n}</span>
                  <span className="en-illu-lines"><span /><span /></span>
                  <span className="en-illu-thumb" />
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}
